import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
} from '@mui/material';
import { Article, Category, Unit, ArticleCreate, ArticleUpdate } from '../types';
import { getArticles, createArticle, updateArticle, deleteArticle, categoriesAPI, unitsAPI } from '../services/api';
import { extractErrorMessage } from '../utils/errorHandler';

interface ArticleFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  article?: Article | null;
  onShowMessage?: (type: 'success' | 'error', message: string) => void;
}

const ArticleForm: React.FC<ArticleFormProps> = ({
  open,
  onClose,
  onSuccess,
  article,
  onShowMessage
}) => {
  const [formData, setFormData] = useState({
    reference: '',
    designation: '',
    category_id: '',
    unit_id: '',
    seuil_alerte: '1',
    date_creation: new Date().toISOString().split('T')[0],
  });
  
  const [categories, setCategories] = useState<Category[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchDropdownData = async () => {
    try {
      const [categoriesData, unitsData] = await Promise.all([
        categoriesAPI.getAll(),
        unitsAPI.getAll()
      ]);
      setCategories(categoriesData);
      setUnits(unitsData);
    } catch (err) {
      console.error('Error fetching dropdown data:', err);
    }
  };

  useEffect(() => {
    if (open) {
      fetchDropdownData();
      
      if (article) {
        setFormData({
          reference: article.reference,
          designation: article.designation,
          category_id: article.category_id.toString(),
          unit_id: article.unit_id.toString(),
          seuil_alerte: article.seuil_alerte.toString(),
          date_creation: article.date_creation,
        });
      } else {
        setFormData({
          reference: '',
          designation: '',
          category_id: '',
          unit_id: '',
          seuil_alerte: '1',
          date_creation: new Date().toISOString().split('T')[0],
        });
      }
      
    }
  }, [open, article]);

  const handleChange = (field: string) => (event: any) => {
    let value = event.target.value;

    // For seuil_alerte, allow digits with comma or dot
    if (field === 'seuil_alerte') {
      // Allow digits with at most one comma or dot
      if (value === '' || /^\d*[.,]?\d*$/.test(value)) {
        setFormData({
          ...formData,
          [field]: value,
        });
      }
      return;
    }

    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handleBlur = (field: string) => (event: any) => {
    // For seuil_alerte, convert to number on blur
    if (field === 'seuil_alerte') {
      const value = event.target.value;
      if (value && value !== '') {
        const numValue = parseFloat(value.replace(',', '.'));
        if (!isNaN(numValue)) {
          setFormData({
            ...formData,
            [field]: numValue.toString(),
          });
        }
      }
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const articleData: ArticleCreate | ArticleUpdate = {
        reference: formData.reference,
        designation: formData.designation,
        category_id: parseInt(formData.category_id),
        unit_id: parseInt(formData.unit_id),
        seuil_alerte: parseFloat(formData.seuil_alerte.replace(',', '.')),
        date_creation: formData.date_creation,
      };

      if (article) {
        await updateArticle(article.id, articleData);
        onShowMessage?.('success', 'Article modifié avec succès');
      } else {
        await createArticle(articleData as ArticleCreate);
        onShowMessage?.('success', 'Article créé avec succès');
      }

      onSuccess();
    } catch (err: any) {
      const errorMessage = extractErrorMessage(err);
      onShowMessage?.('error', errorMessage);
      console.error('Error saving article:', err);
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    return (
      formData.reference.trim() &&
      formData.designation.trim() &&
      formData.category_id &&
      formData.unit_id
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {article ? "Modifier l'article" : 'Nouvel article'}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ pt: 1 }}>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                variant="filled"
                label="Référence"
                value={formData.reference}
                onChange={handleChange('reference')}
                fullWidth
                required
                placeholder="ex: TLN251252"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                variant="filled"
                label="Date de création"
                type="date"
                value={formData.date_creation}
                onChange={handleChange('date_creation')}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                variant="filled"
                label="Désignation"
                value={formData.designation}
                onChange={handleChange('designation')}
                fullWidth
                required
                placeholder="ex: TOLE NOIR 2500x1250X2"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required variant="filled">
                <InputLabel>Catégorie</InputLabel>
                <Select
                  value={formData.category_id}
                  onChange={handleChange('category_id')}
                  label="Catégorie"
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id.toString()}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required variant="filled">
                <InputLabel>Unité</InputLabel>
                <Select
                  value={formData.unit_id}
                  onChange={handleChange('unit_id')}
                  label="Unité"
                >
                  {units.map((unit) => (
                    <MenuItem key={unit.id} value={unit.id.toString()}>
                      {unit.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                variant="filled"
                label="Seuil d'alerte"
                type="text"
                value={formData.seuil_alerte}
                onChange={handleChange('seuil_alerte')}
                onBlur={handleBlur('seuil_alerte')}
                fullWidth
                placeholder="1"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !isFormValid()}
        >
          {loading ? 'Sauvegarde...' : 'Sauvegarder'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ArticleForm;